import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Body1,
  Caption1,
  Card,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Subtitle1,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { SAMPLE_VARIANTS, type PeopleImportView, type PeopleRowRecord, type SampleVariant } from '@lg/core';
import { SHEETS, type PeopleSheet, type TemplateConfig } from '@lg/shared';
import { useApi } from '../api/client';
import { errorMessage, saveFile } from '../ui/format';
import { CoverageMatrixView } from './CoverageMatrixView';
import { IssuesPanel } from './IssuesPanel';
import { RoutingPreview, type PreviewRequest } from './RoutingPreview';
import { RowDrawer } from './RowDrawer';
import { WhoIsWho } from './WhoIsWho';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  head: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  spacer: { flex: 1 },
  tableWrap: { overflowX: 'auto' },
  // Size columns to their content (Fluent defaults to equal fixed widths, which overlapped UPNs and dates).
  table: { tableLayout: 'auto', width: '100%' },
  cell: { whiteSpace: 'nowrap' },
  issuesCell: { whiteSpace: 'normal', minWidth: '220px' },
  row: { cursor: 'pointer' },
  errorRow: { backgroundColor: tokens.colorPaletteRedBackground1 },
  warnRow: { backgroundColor: tokens.colorPaletteYellowBackground1 },
  errorCell: { color: tokens.colorPaletteRedForeground1, fontWeight: tokens.fontWeightSemibold },
  tabLabel: { display: 'inline-flex', gap: tokens.spacingHorizontalXS, alignItems: 'center' },
  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    alignItems: 'center',
  },
});

export function PeopleWorkspace({
  requestId,
  legalEntityId,
  editable,
}: {
  requestId: string;
  legalEntityId: string | null;
  editable: boolean;
}) {
  const styles = useStyles();
  const api = useApi();
  const qc = useQueryClient();
  const request = useQuery({ queryKey: ['request', requestId], queryFn: () => api.getRequest(requestId) });
  const importId = request.data?.peopleImport?.id ?? null;
  const view = useQuery({
    queryKey: ['peopleImport', importId],
    queryFn: () => api.getPeopleImport(importId ?? ''),
    enabled: importId !== null,
  });
  // 'overview' is the joined Who's who view; the rest are the six import sheets.
  const [tab, setTab] = useState<PeopleSheet | 'overview'>('overview');
  const sheet: PeopleSheet = tab === 'overview' ? 'positions' : tab;
  const setSheet = (s: PeopleSheet) => setTab(s);
  const [editing, setEditing] = useState<{ row: PeopleRowRecord | null; sheet: PeopleSheet } | null>(null);
  const [preview, setPreview] = useState<PreviewRequest | null>(null);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  const applyView = (v: PeopleImportView) => {
    qc.setQueryData(['peopleImport', v.import.id], v);
    void qc.invalidateQueries({ queryKey: ['request', requestId] });
    void qc.invalidateQueries({ queryKey: ['requests'] });
    void qc.invalidateQueries({ queryKey: ['packages', requestId] });
  };

  const load = useMutation({
    mutationFn: (variant: SampleVariant | 'empty') =>
      api.createPeopleImport(
        requestId,
        variant === 'empty' ? { kind: 'empty' } : { kind: 'sample', variant },
      ),
    onSuccess: (v) => {
      setFileErrors([]);
      applyView(v);
    },
  });
  const upload = useMutation({
    mutationFn: async (files: FileList) =>
      api.uploadPeopleFiles(
        requestId,
        await Promise.all(
          [...files].map(async (f) => ({ name: f.name, bytes: new Uint8Array(await f.arrayBuffer()) })),
        ),
      ),
    onSuccess: (r) => {
      setFileErrors(r.fileErrors);
      if (r.view) applyView(r.view);
    },
  });
  const validate = useMutation({
    mutationFn: () => api.validatePeopleImport(importId ?? ''),
    onSuccess: applyView,
  });
  const download = useMutation({
    mutationFn: async (what: 'template' | SampleVariant) =>
      saveFile(
        what === 'template'
          ? await api.peopleTemplateFile(request.data?.template.id ?? 'LTPL')
          : await api.sampleFile(requestId, what),
      ),
  });

  const busy = load.isPending || upload.isPending || validate.isPending;
  const mutationError = load.error ?? upload.error ?? validate.error ?? download.error;
  const v = view.data;
  const template: TemplateConfig | undefined = request.data?.template;

  const toolbar = (
    <Toolbar aria-label="People import actions" size="small">
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <ToolbarButton disabled={!editable || busy || !legalEntityId}>Load sample data</ToolbarButton>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {SAMPLE_VARIANTS.map((s) => (
              <MenuItem key={s.id} onClick={() => load.mutate(s.id)}>
                {s.label}
              </MenuItem>
            ))}
            <MenuItem onClick={() => load.mutate('empty')}>Start empty (enter in the grid)</MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
      <ToolbarButton
        disabled={!editable || busy || !legalEntityId}
        onClick={() => fileInput.current?.click()}
      >
        Upload file
      </ToolbarButton>
      <input
        ref={fileInput}
        type="file"
        accept=".xlsx,.csv"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) upload.mutate(e.target.files);
          e.target.value = '';
        }}
      />
      <ToolbarDivider />
      <ToolbarButton onClick={() => download.mutate('template')}>Download template</ToolbarButton>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <ToolbarButton disabled={!legalEntityId}>Download sample file</ToolbarButton>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {SAMPLE_VARIANTS.map((s) => (
              <MenuItem key={s.id} onClick={() => download.mutate(s.id)}>
                {s.file}
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
      <ToolbarDivider />
      <ToolbarButton disabled={!editable || !v || busy} onClick={() => setEditing({ row: null, sheet })}>
        Add row
      </ToolbarButton>
      <ToolbarButton disabled={!v || busy} onClick={() => validate.mutate()}>
        Validate
      </ToolbarButton>
    </Toolbar>
  );

  if (request.isPending) return <Spinner label="Loading…" />;

  return (
    <div className={styles.stack}>
      <Card className={styles.card}>
        <div className={styles.head}>
          <Subtitle1 as="h2">People and approvals</Subtitle1>
          {v && <StatusBadge view={v} />}
          <span className={styles.spacer} />
          {busy && <Spinner size="tiny" label="Checking against D365 and Entra ID…" />}
        </div>
        <Body1>
          Who works at the venue, which position each person holds, who they report to, their approval limits
          and security roles. Workflows route by this structure, so the same workflow imports into every venue
          unchanged.
        </Body1>
        {toolbar}
        {!legalEntityId && (
          <MessageBar intent="info" layout="multiline">
            <MessageBarBody>Enter the legal entity ID first; position IDs are built from it.</MessageBarBody>
          </MessageBar>
        )}
        {mutationError && (
          <MessageBar intent="error">
            <MessageBarBody>{errorMessage(mutationError)}</MessageBarBody>
          </MessageBar>
        )}
        {fileErrors.length > 0 && (
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>File problems</MessageBarTitle>
              {fileErrors.join(' · ')}
            </MessageBarBody>
          </MessageBar>
        )}

        {!importId && (
          <div className={styles.empty}>
            <Body1>
              No people data yet. Load a sample, upload the Excel template, or start empty and use the grid.
            </Body1>
          </div>
        )}
        {importId && view.isPending && <Spinner label="Loading people import…" />}

        {v && (
          <>
            <TabList
              selectedValue={tab}
              onTabSelect={(_, d) => setTab(d.value as PeopleSheet | 'overview')}
              style={{ flexWrap: 'wrap' }}
            >
              <Tab value="overview">Who's who</Tab>
              {SHEETS.map((s) => {
                const c = v.counts[s.sheet];
                return (
                  <Tab key={s.sheet} value={s.sheet}>
                    <span className={styles.tabLabel}>
                      {s.title}
                      <Badge size="small" appearance="tint" color="informative">
                        {c.rows}
                      </Badge>
                      {c.errors > 0 && (
                        <Badge size="small" color="danger">
                          {c.errors}
                        </Badge>
                      )}
                      {c.errors === 0 && c.warnings > 0 && (
                        <Badge size="small" color="warning">
                          {c.warnings}
                        </Badge>
                      )}
                    </span>
                  </Tab>
                );
              })}
            </TabList>
            {tab === 'overview' ? (
              <WhoIsWho view={v} onOpen={(row) => setEditing({ row, sheet: 'positions' })} />
            ) : (
              <>
                <Caption1>
                  {SHEETS.find((s) => s.sheet === sheet)?.description} Click a row to edit it.
                </Caption1>
                <SheetTable view={v} sheet={sheet} onOpen={(row) => setEditing({ row, sheet })} />
              </>
            )}
          </>
        )}
      </Card>

      {v && (
        <IssuesPanel
          issues={v.issues}
          onOpenRow={(s, rowNumber) => {
            const row = v.rows.find((r) => r.sheet === s && r.rowNumber === rowNumber);
            if (row) {
              setSheet(s);
              setEditing({ row, sheet: s });
            }
          }}
        />
      )}

      {v && v.coverage && (
        <CoverageMatrixView
          coverage={v.coverage}
          onPick={(documentType, amountUsd, submitterUpn) =>
            setPreview({
              documentType,
              amountUsd,
              submitterUpn: submitterUpn ?? v.submitters[0]?.upn ?? '',
              nonce: Date.now(),
            })
          }
        />
      )}

      {v && <RoutingPreview view={v} request={preview} />}

      {editing && v && template && (
        <RowDrawer
          key={editing.row?.id ?? `new-${editing.sheet}`}
          view={v}
          row={editing.row}
          sheet={editing.sheet}
          template={template}
          editable={v.canEdit}
          onClose={() => setEditing(null)}
          onSaved={(next) => {
            applyView(next);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function StatusBadge({ view }: { view: PeopleImportView }) {
  const errors = view.issues.filter((i) => i.severity === 'error').length;
  const warnings = view.issues.length - errors;
  if (view.import.status === 'Valid') {
    return (
      <Badge color="success">Valid{warnings ? ` · ${warnings} warning${warnings > 1 ? 's' : ''}` : ''}</Badge>
    );
  }
  if (view.import.status === 'Invalid')
    return (
      <Badge color="danger">
        Invalid · {errors} error{errors === 1 ? '' : 's'}
      </Badge>
    );
  return <Badge color="informative">{view.import.status}</Badge>;
}

function SheetTable({
  view,
  sheet,
  onOpen,
}: {
  view: PeopleImportView;
  sheet: PeopleSheet;
  onOpen: (row: PeopleRowRecord) => void;
}) {
  const styles = useStyles();
  const def = SHEETS.find((s) => s.sheet === sheet);
  const rows = view.rows.filter((r) => r.sheet === sheet);
  if (!def) return null;
  return (
    <div className={styles.tableWrap}>
      <Table size="small" className={styles.table} aria-label={def.title}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: 48 }}>Row</TableHeaderCell>
            {def.columns.map((c) => (
              <TableHeaderCell key={c.key}>{c.label}</TableHeaderCell>
            ))}
            <TableHeaderCell>Issues</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={def.columns.length + 2}>No rows</TableCell>
            </TableRow>
          )}
          {rows.map((r) => {
            const hasError = r.issues.some((i) => i.severity === 'error');
            const badColumns = new Set(r.issues.filter((i) => i.severity === 'error').map((i) => i.column));
            return (
              <TableRow
                key={r.id}
                className={mergeClasses(
                  styles.row,
                  hasError ? styles.errorRow : r.issues.length ? styles.warnRow : undefined,
                )}
                onClick={() => onOpen(r)}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onOpen(r)}
              >
                <TableCell>{r.rowNumber}</TableCell>
                {def.columns.map((c) => {
                  const value = r.data[c.key];
                  const text =
                    typeof value === 'boolean'
                      ? value
                        ? 'yes'
                        : 'no'
                      : typeof value === 'number'
                        ? value.toLocaleString('en-US')
                        : String(value ?? '');
                  return (
                    <TableCell
                      key={c.key}
                      className={mergeClasses(styles.cell, badColumns.has(c.key) && styles.errorCell)}
                    >
                      {text}
                    </TableCell>
                  );
                })}
                <TableCell className={styles.issuesCell}>
                  {r.issues.map((i) => i.message).join('; ')}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
